import React, { useState, useMemo } from 'react';
import {
  Search, Filter, Download, Eye, Trash2, Plus, X,
  FileText, Target, ShieldCheck, BarChart3, BookOpen,
  CheckCircle, Clock, AlertCircle, ChevronDown, RefreshCw,
  Building2, Calendar, SortAsc, SortDesc,
} from 'lucide-react';
import ReportPreviewModal from './ReportPreviewModal';

// ─── Sample Clients ───────────────────────────────────────────────────────────

const CLIENTS = [
  { uuid: 'CLT-2501', name: 'Acme Corporation',    industry: 'Manufacturing'      },
  { uuid: 'CLT-2502', name: 'TechStart Inc.',       industry: 'Technology'         },
  { uuid: 'CLT-2503', name: 'Global Finance Ltd.',  industry: 'Finance'            },
  { uuid: 'CLT-2504', name: 'SecureNet Systems',    industry: 'Cybersecurity'      },
  { uuid: 'CLT-2505', name: 'HealthCare Partners',  industry: 'Healthcare'         },
];

// ─── Report Types ─────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { id: 'all',       label: 'All Types'             },
  { id: 'mitre',     label: 'MITRE Assessment'       },
  { id: 'posture',   label: 'Security Posture'       },
  { id: 'incident',  label: 'Incident Summary'       },
  { id: 'threat',    label: 'Threat Intelligence'    },
  { id: 'compliance',label: 'Compliance Audit'       },
];

const TYPE_META = {
  mitre:      { label: 'MITRE Assessment',    icon: Target,    color: '#f97316', bg: 'rgba(249,115,22,0.1)',   previewable: true },
  posture:    { label: 'Security Posture',    icon: ShieldCheck, color: '#34d399', bg: 'rgba(52,211,153,0.1)', previewable: true },
  incident:   { label: 'Incident Summary',    icon: BarChart3, color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',   previewable: true },
  threat:     { label: 'Threat Intelligence', icon: BookOpen,  color: '#a78bfa', bg: 'rgba(167,139,250,0.1)',  previewable: false },
  compliance: { label: 'Compliance Audit',    icon: FileText,  color: '#7c3aed', bg: 'rgba(124,58,237,0.1)',   previewable: true },
};

// ─── Sample Report List (simulated from Azure Sentinel) ───────────────────────

let _nextId = 1000;
const mkId = () => `RPT-${++_nextId}`;

const INITIAL_REPORTS = [
  /* Acme */
  { id: mkId(), clientUuid: 'CLT-2501', type: 'mitre',      name: 'MITRE ATT&CK Assessment – May 2026',     period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-25T09:12:00Z', size: '2.3 MB', sentinelWorkspace: 'acme-prod-la' },
  { id: mkId(), clientUuid: 'CLT-2501', type: 'posture',    name: 'Security Posture Report – May 2026',      period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-25T09:15:00Z', size: '1.8 MB', sentinelWorkspace: 'acme-prod-la' },
  { id: mkId(), clientUuid: 'CLT-2501', type: 'incident',   name: 'Incident Summary – Week 21',              period: 'May 19–25',   status: 'ready',      generatedAt: '2026-05-25T08:00:00Z', size: '0.9 MB', sentinelWorkspace: 'acme-prod-la' },
  { id: mkId(), clientUuid: 'CLT-2501', type: 'compliance', name: 'Compliance Audit Q2 2026',                period: 'Apr–Jun 2026',status: 'generating', generatedAt: null,                    size: null,     sentinelWorkspace: 'acme-prod-la' },
  { id: mkId(), clientUuid: 'CLT-2501', type: 'mitre',      name: 'MITRE ATT&CK Assessment – Apr 2026',     period: 'Apr 2026',    status: 'ready',      generatedAt: '2026-04-30T10:05:00Z', size: '2.1 MB', sentinelWorkspace: 'acme-prod-la' },
  { id: mkId(), clientUuid: 'CLT-2501', type: 'posture',    name: 'Security Posture Report – Apr 2026',      period: 'Apr 2026',    status: 'ready',      generatedAt: '2026-04-30T10:10:00Z', size: '1.7 MB', sentinelWorkspace: 'acme-prod-la' },
  { id: mkId(), clientUuid: 'CLT-2501', type: 'threat',     name: 'Threat Intelligence Digest – Week 20',    period: 'May 12–18',   status: 'failed',     generatedAt: '2026-05-18T07:50:00Z', size: null,     sentinelWorkspace: 'acme-prod-la' },

  /* TechStart */
  { id: mkId(), clientUuid: 'CLT-2502', type: 'mitre',      name: 'MITRE ATT&CK Assessment – May 2026',     period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-24T14:30:00Z', size: '1.9 MB', sentinelWorkspace: 'techstart-sentinel' },
  { id: mkId(), clientUuid: 'CLT-2502', type: 'posture',    name: 'Security Posture Report – May 2026',      period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-24T14:35:00Z', size: '1.5 MB', sentinelWorkspace: 'techstart-sentinel' },
  { id: mkId(), clientUuid: 'CLT-2502', type: 'incident',   name: 'Incident Summary – Week 21',              period: 'May 19–25',   status: 'generating', generatedAt: null,                    size: null,     sentinelWorkspace: 'techstart-sentinel' },
  { id: mkId(), clientUuid: 'CLT-2502', type: 'compliance', name: 'Compliance Audit – ISO 27001 May 2026',  period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-20T11:00:00Z', size: '3.1 MB', sentinelWorkspace: 'techstart-sentinel' },

  /* Global Finance */
  { id: mkId(), clientUuid: 'CLT-2503', type: 'mitre',      name: 'MITRE ATT&CK Assessment – May 2026',     period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-23T16:00:00Z', size: '2.6 MB', sentinelWorkspace: 'gf-la-workspace' },
  { id: mkId(), clientUuid: 'CLT-2503', type: 'posture',    name: 'Security Posture Report – May 2026',      period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-23T16:05:00Z', size: '2.0 MB', sentinelWorkspace: 'gf-la-workspace' },
  { id: mkId(), clientUuid: 'CLT-2503', type: 'compliance', name: 'PCI-DSS Compliance Report Q2 2026',      period: 'Apr–Jun 2026',status: 'ready',      generatedAt: '2026-05-01T09:00:00Z', size: '4.2 MB', sentinelWorkspace: 'gf-la-workspace' },
  { id: mkId(), clientUuid: 'CLT-2503', type: 'threat',     name: 'Threat Intelligence Digest – May 2026',  period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-22T08:30:00Z', size: '1.2 MB', sentinelWorkspace: 'gf-la-workspace' },
  { id: mkId(), clientUuid: 'CLT-2503', type: 'incident',   name: 'Incident Summary – Week 21',              period: 'May 19–25',   status: 'ready',      generatedAt: '2026-05-25T07:45:00Z', size: '0.8 MB', sentinelWorkspace: 'gf-la-workspace' },

  /* SecureNet */
  { id: mkId(), clientUuid: 'CLT-2504', type: 'mitre',      name: 'MITRE ATT&CK Assessment – May 2026',     period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-25T11:00:00Z', size: '2.4 MB', sentinelWorkspace: 'sn-sentinel-ws'  },
  { id: mkId(), clientUuid: 'CLT-2504', type: 'posture',    name: 'Security Posture Report – Q2 2026',       period: 'Apr–Jun 2026',status: 'generating', generatedAt: null,                    size: null,     sentinelWorkspace: 'sn-sentinel-ws'  },
  { id: mkId(), clientUuid: 'CLT-2504', type: 'threat',     name: 'Threat Intelligence Digest – Week 21',   period: 'May 19–25',   status: 'ready',      generatedAt: '2026-05-25T06:00:00Z', size: '1.1 MB', sentinelWorkspace: 'sn-sentinel-ws'  },

  /* HealthCare */
  { id: mkId(), clientUuid: 'CLT-2505', type: 'mitre',      name: 'MITRE ATT&CK Assessment – May 2026',     period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-24T13:00:00Z', size: '1.7 MB', sentinelWorkspace: 'hcp-la-main'     },
  { id: mkId(), clientUuid: 'CLT-2505', type: 'compliance', name: 'HIPAA Compliance Report – May 2026',     period: 'May 2026',    status: 'ready',      generatedAt: '2026-05-15T10:30:00Z', size: '5.8 MB', sentinelWorkspace: 'hcp-la-main'     },
  { id: mkId(), clientUuid: 'CLT-2505', type: 'incident',   name: 'Incident Summary – Week 21',              period: 'May 19–25',   status: 'failed',     generatedAt: '2026-05-25T07:00:00Z', size: null,     sentinelWorkspace: 'hcp-la-main'     },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_META = {
  ready:      { label: 'Ready',      color: '#34d399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.25)',  icon: CheckCircle  },
  generating: { label: 'Generating', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',  border: 'rgba(96,165,250,0.25)',  icon: Clock        },
  failed:     { label: 'Failed',     color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)',   icon: AlertCircle  },
};

function StatusBadge({ status }) {
  const s = STATUS_META[status] || STATUS_META.generating;
  const Icon = s.icon;
  return (
    <span className="rl-status" style={{ color: s.color, background: s.bg, borderColor: s.border }}>
      <Icon size={10} className={status === 'generating' ? 'rl-spin' : ''} />
      {s.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const m = TYPE_META[type];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span className="rl-type-badge" style={{ color: m.color, background: m.bg }}>
      <Icon size={10} /> {m.label}
    </span>
  );
}

// ─── Generate Report Modal ────────────────────────────────────────────────────

function GenerateModal({ onClose, onGenerate }) {
  const [form, setForm] = useState({ clientUuid: '', type: 'mitre', period: 'monthly', customFrom: '', customTo: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleGenerate = () => {
    if (!form.clientUuid || !form.type) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      setTimeout(() => { onGenerate(form); onClose(); }, 1200);
    }, 1800);
  };

  const periodOpts = [
    { value: 'weekly',    label: 'This Week'        },
    { value: 'monthly',   label: 'This Month'       },
    { value: 'quarterly', label: 'This Quarter'     },
    { value: 'custom',    label: 'Custom Range'     },
  ];

  return (
    <div className="rl-modal-overlay" onClick={onClose}>
      <div className="rl-modal" onClick={e => e.stopPropagation()}>
        <div className="rl-modal__header">
          <div>
            <h2 className="rl-modal__title">Generate Security Report</h2>
            <p className="rl-modal__sub">Report will be generated from Azure Sentinel data</p>
          </div>
          <button className="rl-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        <div className="rl-modal__body">
          {done ? (
            <div className="rl-modal__success">
              <CheckCircle size={32} />
              <p>Report queued for generation.</p>
              <small>It will appear in the list once ready (usually under 2 minutes).</small>
            </div>
          ) : (
            <>
              <div className="rl-form-row">
                <label className="rl-form-label">Client <span className="rl-form-req">*</span></label>
                <div className="rl-select-wrap">
                  <Building2 size={13} className="rl-select-icon" />
                  <select className="rl-select" value={form.clientUuid} onChange={e => set('clientUuid', e.target.value)}>
                    <option value="">Select client…</option>
                    {CLIENTS.map(c => <option key={c.uuid} value={c.uuid}>{c.name} — {c.uuid}</option>)}
                  </select>
                  <ChevronDown size={12} className="rl-select-chevron" />
                </div>
              </div>

              <div className="rl-form-row">
                <label className="rl-form-label">Report Type <span className="rl-form-req">*</span></label>
                <div className="rl-type-grid">
                  {REPORT_TYPES.filter(t => t.id !== 'all').map(t => {
                    const m = TYPE_META[t.id];
                    const Icon = m?.icon || FileText;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`rl-type-card${form.type === t.id ? ' rl-type-card--active' : ''}`}
                        onClick={() => set('type', t.id)}
                        style={{ '--tc': m?.color || '#94a3b8' }}
                      >
                        <Icon size={16} />
                        <span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rl-form-row">
                <label className="rl-form-label">Reporting Period</label>
                <div className="rl-period-row">
                  {periodOpts.map(p => (
                    <button key={p.value} type="button" className={`rl-period-btn${form.period === p.value ? ' rl-period-btn--active' : ''}`} onClick={() => set('period', p.value)}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {form.period === 'custom' && (
                  <div className="rl-date-range">
                    <input className="rl-input" type="date" value={form.customFrom} onChange={e => set('customFrom', e.target.value)} />
                    <span className="rl-date-sep">to</span>
                    <input className="rl-input" type="date" value={form.customTo}   onChange={e => set('customTo', e.target.value)} />
                  </div>
                )}
              </div>

              <div className="rl-form-row">
                <label className="rl-form-label">Azure Sentinel Workspace</label>
                <div className="rl-info-box">
                  <span>Data will be pulled from the workspace configured for the selected client in VorXOC's Sentinel connector.</span>
                </div>
              </div>
            </>
          )}
        </div>

        {!done && (
          <div className="rl-modal__footer">
            <button className="btn btn--outline btn--sm" onClick={onClose}>Cancel</button>
            <button
              className="btn btn--primary btn--sm"
              onClick={handleGenerate}
              disabled={!form.clientUuid || submitting}
            >
              {submitting ? <><RefreshCw size={12} className="rl-spin" /> Queuing…</> : <><Plus size={12} /> Generate Report</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ReportLibrary ───────────────────────────────────────────────────────

export default function ReportLibrary() {
  const [reports, setReports]         = useState(INITIAL_REPORTS);
  const [clientFilter, setClientFilter] = useState('all');
  const [typeFilter, setTypeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]           = useState('');
  const [sortKey, setSortKey]         = useState('generatedAt');
  const [sortDir, setSortDir]         = useState('desc');
  const [showGenerate, setShowGenerate] = useState(false);
  const [previewType, setPreviewType] = useState(null);
  const [deletingId, setDeletingId]   = useState(null);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let r = [...reports];
    if (clientFilter !== 'all')  r = r.filter(x => x.clientUuid === clientFilter);
    if (typeFilter   !== 'all')  r = r.filter(x => x.type       === typeFilter);
    if (statusFilter !== 'all')  r = r.filter(x => x.status     === statusFilter);
    if (search.trim())           r = r.filter(x => x.name.toLowerCase().includes(search.toLowerCase()) || x.clientUuid.toLowerCase().includes(search.toLowerCase()));
    r.sort((a, b) => {
      const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 :  1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return r;
  }, [reports, clientFilter, typeFilter, statusFilter, search, sortKey, sortDir]);

  // Stats for selected client (or all)
  const baseReports = clientFilter === 'all' ? reports : reports.filter(r => r.clientUuid === clientFilter);
  const stats = {
    total:      baseReports.length,
    ready:      baseReports.filter(r => r.status === 'ready').length,
    generating: baseReports.filter(r => r.status === 'generating').length,
    failed:     baseReports.filter(r => r.status === 'failed').length,
  };

  const handleGenerate = (form) => {
    const client = CLIENTS.find(c => c.uuid === form.clientUuid);
    const typeLabel = REPORT_TYPES.find(t => t.id === form.type)?.label || form.type;
    const newReport = {
      id: `RPT-${Date.now()}`,
      clientUuid: form.clientUuid,
      type: form.type,
      name: `${typeLabel} – ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
      period: form.period === 'custom' ? `${form.customFrom} – ${form.customTo}` : form.period,
      status: 'generating',
      generatedAt: null,
      size: null,
      sentinelWorkspace: client?.uuid?.toLowerCase().replace('-', '') + '-ws',
    };
    setReports(prev => [newReport, ...prev]);
    // Simulate completion after 4s
    setTimeout(() => {
      setReports(prev => prev.map(r => r.id === newReport.id
        ? { ...r, status: 'ready', generatedAt: new Date().toISOString(), size: `${(Math.random() * 3 + 0.8).toFixed(1)} MB` }
        : r
      ));
    }, 4000);
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setTimeout(() => {
      setReports(prev => prev.filter(r => r.id !== id));
      setDeletingId(null);
    }, 600);
  };

  const handleDownload = (report) => {
    if (report.status !== 'ready') return;
    const m = TYPE_META[report.type];
    if (m?.previewable) {
      setPreviewType(report.type);
    } else {
      // Simulate download of non-previewable reports
      const a = document.createElement('a');
      a.href = `data:text/plain;charset=utf-8,${encodeURIComponent(`Report: ${report.name}\nClient: ${report.clientUuid}\nGenerated: ${report.generatedAt}`)}`
      a.download = `${report.name.replace(/\s+/g, '_')}.txt`;
      a.click();
    }
  };

  const SortBtn = ({ k, label }) => {
    const active = sortKey === k;
    const Icon = active && sortDir === 'asc' ? SortAsc : SortDesc;
    return (
      <button className={`rl-th-sort${active ? ' rl-th-sort--active' : ''}`} onClick={() => toggleSort(k)}>
        {label} <Icon size={11} />
      </button>
    );
  };

  const selectedClient = CLIENTS.find(c => c.uuid === clientFilter);

  return (
    <div className="report-library">
      {previewType && <ReportPreviewModal reportType={previewType} onClose={() => setPreviewType(null)} />}
      {showGenerate && <GenerateModal onClose={() => setShowGenerate(false)} onGenerate={handleGenerate} />}

      {/* ── Header ── */}
      <div className="rl-header">
        <div className="rl-header__left">
          <h2 className="rl-header__title">Report Library</h2>
          <p className="rl-header__sub">Azure Sentinel-backed security reports · all clients</p>
        </div>
        <button className="btn btn--primary btn--sm" onClick={() => setShowGenerate(true)}>
          <Plus size={13} /> Generate Report
        </button>
      </div>

      {/* ── Client filter ── */}
      <div className="rl-client-bar">
        <div className="rl-client-bar__label"><Building2 size={13} /> Filter by Client</div>
        <div className="rl-client-chips">
          <button className={`rl-client-chip${clientFilter === 'all' ? ' rl-client-chip--active' : ''}`} onClick={() => setClientFilter('all')}>
            All Clients <span className="rl-chip-count">{reports.length}</span>
          </button>
          {CLIENTS.map(c => {
            const count = reports.filter(r => r.clientUuid === c.uuid).length;
            return (
              <button
                key={c.uuid}
                className={`rl-client-chip${clientFilter === c.uuid ? ' rl-client-chip--active' : ''}`}
                onClick={() => setClientFilter(c.uuid)}
              >
                <span className="rl-chip-name">{c.name}</span>
                <span className="rl-chip-uuid">{c.uuid}</span>
                <span className="rl-chip-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="rl-stats">
        <div className="rl-stat">
          <span className="rl-stat__value">{stats.total}</span>
          <span className="rl-stat__label">Total{selectedClient ? ` for ${selectedClient.name}` : ''}</span>
        </div>
        <div className="rl-stat rl-stat--ready">
          <span className="rl-stat__value">{stats.ready}</span>
          <span className="rl-stat__label">Ready to Download</span>
        </div>
        <div className="rl-stat rl-stat--gen">
          <span className="rl-stat__value">{stats.generating}</span>
          <span className="rl-stat__label">Generating</span>
        </div>
        <div className="rl-stat rl-stat--fail">
          <span className="rl-stat__value">{stats.failed}</span>
          <span className="rl-stat__label">Failed</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="rl-toolbar">
        <div className="intg-search rl-search">
          <Search size={13} className="intg-search__icon" />
          <input className="intg-search__input" placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="rl-clear-search" onClick={() => setSearch('')}><X size={11} /></button>}
        </div>

        <div className="rl-filters">
          <div className="rl-filter-group">
            <Filter size={11} className="rl-filter-icon" />
            <select className="rl-filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              {REPORT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div className="rl-filter-group">
            <select className="rl-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="ready">Ready</option>
              <option value="generating">Generating</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <span className="rl-result-count">{filtered.length} report{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Table ── */}
      <div className="rl-table-wrap">
        <table className="rl-table">
          <thead>
            <tr>
              <th className="rl-th"><SortBtn k="name" label="Report Name" /></th>
              <th className="rl-th"><SortBtn k="clientUuid" label="Client" /></th>
              <th className="rl-th"><SortBtn k="type" label="Type" /></th>
              <th className="rl-th">Period</th>
              <th className="rl-th">Sentinel Workspace</th>
              <th className="rl-th"><SortBtn k="status" label="Status" /></th>
              <th className="rl-th"><SortBtn k="generatedAt" label="Generated" /></th>
              <th className="rl-th rl-th--center">Size</th>
              <th className="rl-th rl-th--center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="rl-empty">
                  <FileText size={28} />
                  <p>No reports match the current filters.</p>
                </td>
              </tr>
            ) : filtered.map((r) => {
              const isDeleting = deletingId === r.id;
              const canDownload = r.status === 'ready';
              const client = CLIENTS.find(c => c.uuid === r.clientUuid);
              return (
                <tr key={r.id} className={`rl-row${isDeleting ? ' rl-row--deleting' : ''}`}>
                  <td className="rl-td rl-td--name">
                    <span className="rl-report-id">{r.id}</span>
                    <span className="rl-report-name">{r.name}</span>
                  </td>
                  <td className="rl-td">
                    <span className="rl-client-cell">
                      <span className="rl-client-cell__name">{client?.name || r.clientUuid}</span>
                      <span className="rl-client-cell__uuid">{r.clientUuid}</span>
                    </span>
                  </td>
                  <td className="rl-td"><TypeBadge type={r.type} /></td>
                  <td className="rl-td rl-td--muted">
                    <Calendar size={11} style={{marginRight: 4, opacity: 0.5}} />{r.period}
                  </td>
                  <td className="rl-td rl-td--mono">{r.sentinelWorkspace}</td>
                  <td className="rl-td"><StatusBadge status={r.status} /></td>
                  <td className="rl-td rl-td--muted">{fmtDate(r.generatedAt)}</td>
                  <td className="rl-td rl-td--center rl-td--muted">{r.size || '—'}</td>
                  <td className="rl-td rl-td--center">
                    <div className="rl-actions">
                      {TYPE_META[r.type]?.previewable && canDownload && (
                        <button className="rl-action-btn rl-action-btn--preview" title="Preview" onClick={() => setPreviewType(r.type)}>
                          <Eye size={13} />
                        </button>
                      )}
                      <button
                        className={`rl-action-btn rl-action-btn--download${!canDownload ? ' rl-action-btn--disabled' : ''}`}
                        title={canDownload ? 'Download' : r.status === 'generating' ? 'Generating…' : 'Failed'}
                        onClick={() => handleDownload(r)}
                        disabled={!canDownload}
                      >
                        <Download size={13} />
                      </button>
                      <button className="rl-action-btn rl-action-btn--delete" title="Delete" onClick={() => handleDelete(r.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
